class ImperiaMerchPdp extends HTMLElement {
  connectedCallback() {
    this.variantSelect = this.querySelector('[data-merch-variant-select]');
    this.mediaTrack = this.querySelector('[data-merch-media-track]');
    this.mediaItems = [...this.querySelectorAll('[data-merch-media]')];
    this.mediaTargets = [...this.querySelectorAll('[data-merch-media-target]')];
    this.previousButton = this.querySelector('[data-merch-media-previous]');
    this.nextButton = this.querySelector('[data-merch-media-next]');
    this.mediaIndex = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.variantSelect?.addEventListener('change', this.handleVariantChange);
    this.previousButton?.addEventListener('click', this.showPreviousMedia);
    this.nextButton?.addEventListener('click', this.showNextMedia);
    this.mediaTrack?.addEventListener('scroll', this.handleMediaScroll, { passive: true });
    this.mediaTrack?.addEventListener('keydown', this.handleMediaKeydown);
    this.mediaTargets.forEach((target) => target.addEventListener('click', this.handleMediaTargetClick));
    this.reducedMotion.addEventListener('change', this.handleMotionPreferenceChange);

    const initialMediaId = this.dataset.mediaId;
    const initialIndex = this.mediaItems.findIndex((item) => item.dataset.mediaId === initialMediaId);
    this.setActiveMedia(initialIndex >= 0 ? initialIndex : 0, false);
    this.syncVariant(this.variantSelect?.selectedOptions[0]);
  }

  disconnectedCallback() {
    this.variantSelect?.removeEventListener('change', this.handleVariantChange);
    this.previousButton?.removeEventListener('click', this.showPreviousMedia);
    this.nextButton?.removeEventListener('click', this.showNextMedia);
    this.mediaTrack?.removeEventListener('scroll', this.handleMediaScroll);
    this.mediaTrack?.removeEventListener('keydown', this.handleMediaKeydown);
    this.mediaTargets.forEach((target) => target.removeEventListener('click', this.handleMediaTargetClick));
    this.reducedMotion?.removeEventListener('change', this.handleMotionPreferenceChange);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  handleVariantChange = (event) => {
    this.syncVariant(event.currentTarget.selectedOptions[0]);
  };

  syncVariant(option) {
    if (!option) return;

    const available = option.dataset.available === 'true';
    const priceAmount = this.querySelector('[data-merch-price]');
    const compare = this.querySelector('[data-merch-compare]');
    const promotions = this.querySelectorAll('[data-merch-promotion]');
    const discount = this.querySelector('[data-merch-discount]');
    const savings = this.querySelector('[data-merch-savings]');
    const availability = this.querySelector('[data-merch-availability]');
    const button = this.querySelector('[ref="addToCartButton"]');
    const buttonText = button?.querySelector('.add-to-cart-text__content');
    const quantityInput = this.querySelector('.imperia-pdp-merch__quantity-selector input[type="number"]');
    const quantityButtons = this.querySelectorAll('.imperia-pdp-merch__quantity-selector .quantity-minus, .imperia-pdp-merch__quantity-selector .quantity-plus');

    if (priceAmount) priceAmount.textContent = option.dataset.price || '';
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }
    promotions.forEach((promotion) => { promotion.hidden = !option.dataset.discount; });
    if (discount) discount.textContent = option.dataset.discount ? `${option.dataset.discount}% OFF` : '';
    if (savings) savings.textContent = option.dataset.savings || '';
    if (availability) availability.textContent = available ? 'DISPONIBLE ONLINE' : 'AGOTADO';
    if (button) button.disabled = !available;
    if (buttonText) buttonText.textContent = available ? 'AGREGAR AL CARRITO' : 'AGOTADO';

    this.dataset.available = String(available);
    this.updateQuantity(quantityInput, quantityButtons, option, available);
    this.updateFeaturedMedia(option.dataset.mediaId);
    this.dispatchEvent(new CustomEvent('variant:changed', { bubbles: true, detail: { option, available } }));
  }

  updateQuantity(input, buttons, option, available) {
    if (!input) return;

    const min = Number(option.dataset.min) || 1;
    const max = Number(option.dataset.max) || null;
    const currentValue = Number(input.value) || min;
    input.min = String(min);
    if (max) input.max = String(max);
    else input.removeAttribute('max');
    input.disabled = !available;

    let nextValue = Math.max(currentValue, min);
    if (max) nextValue = Math.min(nextValue, max);
    input.value = String(nextValue);
    buttons.forEach((button) => { button.disabled = !available; });
  }

  updateFeaturedMedia(mediaId) {
    if (!mediaId || !this.mediaItems.length) return;

    const index = this.mediaItems.findIndex((item) => item.dataset.mediaId === mediaId);
    if (index >= 0) this.setActiveMedia(index, true);
  }

  showPreviousMedia = () => {
    this.setActiveMedia(this.mediaIndex - 1, true);
  };

  showNextMedia = () => {
    this.setActiveMedia(this.mediaIndex + 1, true);
  };

  handleMediaTargetClick = (event) => {
    const index = Number(event.currentTarget.dataset.merchMediaTarget);
    if (Number.isInteger(index)) this.setActiveMedia(index, true);
  };

  handleMediaKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showPreviousMedia();
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showNextMedia();
    }
  };

  handleMediaScroll = () => {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateScrollState();
    });
  };

  handleMotionPreferenceChange = (event) => {
    this.reducedMotion = event.currentTarget;
  };

  setActiveMedia(index, shouldScroll) {
    if (!this.mediaItems.length) return;

    this.mediaIndex = Math.max(0, Math.min(index, this.mediaItems.length - 1));
    const media = this.mediaItems[this.mediaIndex];
    const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';

    if (shouldScroll && media) {
      media.scrollIntoView({ behavior, block: 'nearest', inline: 'start' });
    }

    this.updateScrollState();
  }

  updateScrollState() {
    if (!this.mediaTrack || !this.mediaItems.length) return;

    const trackRect = this.mediaTrack.getBoundingClientRect();
    const isHorizontal = this.mediaTrack.scrollWidth > this.mediaTrack.clientWidth + 1;
    let closestIndex = this.mediaIndex;
    let closestDistance = Infinity;

    this.mediaItems.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const distance = Math.abs((isHorizontal ? rect.left : rect.top) - (isHorizontal ? trackRect.left : trackRect.top));
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.mediaIndex = closestIndex;
    const total = String(this.mediaItems.length).padStart(2, '0');
    const current = String(this.mediaIndex + 1).padStart(2, '0');
    const indexLabel = this.querySelector('[data-merch-media-index]');
    if (indexLabel) indexLabel.textContent = `${current} / ${total}`;

    this.mediaTargets.forEach((target, index) => {
      target.setAttribute('aria-current', String(index === this.mediaIndex));
    });
    if (this.previousButton) this.previousButton.disabled = this.mediaIndex <= 0;
    if (this.nextButton) this.nextButton.disabled = this.mediaIndex >= this.mediaItems.length - 1;
  }
}

if (!customElements.get('imperia-merch-pdp')) {
  customElements.define('imperia-merch-pdp', ImperiaMerchPdp);
}
